import { v4 as uuid } from "uuid";
import { forkJoin, mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';

@Injectable()
export class GroupEntityFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  //</editor-fold>

  //<editor-fold desc="Requests">
  
  protected static copyGroup(store: Store,
                             mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                             id: string,
                             page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                             withAnnotations: boolean,
                             clusterParentUpdate = false) {
    // Check If Group Mapping Exits
    if (mappings.group[id]) {
      return of(undefined);
    }
    
    // Get Data
    const typedGroups = store.selectSnapshot(FlatStates.AnnotationGroupEntityState.typedEntities$);
    
    // Get Group
    const group = structuredClone(typedGroups[id]);
    
    // Update Group Mapping
    mappings.group[id] = uuid();
    
    // Update Group
    group.id = mappings.group[id];
    group.slide = page.id;
    group.order = page.groups.length || 0;
    
    // Update Page
    page.groups.push(group.id);
    
    // Annotation Fix
    const annotations = [];
    if (withAnnotations) {
      // Get Typed Annotations
      const typedAnnotations = store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
      
      // Update Annotations
      const updateAnnotations: {[id: string]: FlatModels.AnnotationEntityModels.AnnotationEntityType} = {};
      group.annotations = group.annotations.reduce((arr, annotation) => {
        // Get Mappings
        const mapping = mappings.annotation[annotation];
        if (mapping) {
          arr.push(mapping);
        }
        /*
        
        // Get Entity
        let entity = updateAnnotations[mapping];
        if (!entity) {
          entity = structuredClone(typedAnnotations[mapping]);
        }
        if (entity) {
          if (entity.groups) {
            entity.groups.push(group.id);
          } else {
            entity.groups = [group.id];
          }
          updateAnnotations[mapping] = entity;
        }
        */
        
        return arr;
      }, [] as string[]);
    } else {
      group.annotations = [];
    }
    
    // Copy Group
    return of(group.id).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationGroupEntityActions.AddEntity(group))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
      mergeMap(() => of(group.id))
    )
  }
  
  static copyGroups(store: Store,
                    mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                    ids: string[],
                    page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                    withAnnotations: boolean,
                    clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Sort Groups
    const typedGroups = store.selectSnapshot(FlatStates.AnnotationGroupEntityState.typedEntities$)
    const sortedIds = ids.reduce((arr, id) => {
      const entity = typedGroups[id];
      if (entity) {
        arr.push(entity);
      }
      return arr;
    }, [] as FlatModels.GroupEntityModels.Group[])
    .sort((a, b) => a.order - b.order)
    .map((group) => group.id);
    
    // Copy Groups
    return forkJoin(sortedIds.map(id => GroupEntityFacade.copyGroup(store, mappings, id, page, withAnnotations, true))).pipe(
      mergeMap((newIds) => {
        const selectIds = newIds.reduce((arr, id) => {
          if (id !== undefined) {
            arr.push(id);
          }
          return arr;
        }, [] as string[]);
        return of(selectIds);
      }),
      mergeMap((selectIds) => {
        if (clusterParentUpdate) {
          return of(selectIds);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page)).pipe(
          mergeMap(() => of(selectIds))
        );
      }),
    )
  }

  static removeGroup(store: Store,
                     id: string,
                     clusterParentUpdate = false) {
    // Get Data
    const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
    const typedGroups = store.selectSnapshot(FlatStates.AnnotationGroupEntityState.typedEntities$);

    // Get Group
    const group = structuredClone(typedGroups[id]);

    // Get Page
    const page = structuredClone(typedPages[group.slide]) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
    page.groups.splice(page.groups.findIndex(id => id === group.id), 1);

    // Remove Group
    return store.dispatch(new FlatActions.BulkEditAuthorActions.RemoveGroup(id)).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.AnnotationGroupEntityActions.RemoveEntity(group))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.AnnotationGroupEntityActions.RearrangeGroups(page.groups)).pipe(
          mergeMap(() =>
            store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
          )
        );
      })
    );
  }

  static removeGroups(store: Store,
                      ids: string[]) {
    if (ids.length === 0) {
      return of([]);
    }

    // Remove Groups
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => GroupEntityFacade.removeGroup(store, id, true)))
      ),
      mergeMap((data: {data: Store, page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training}[]) => {
        const pageMap = data.reduce((arr, clusterParentUpdate) => {
          const page = structuredClone(arr.get(clusterParentUpdate.page.id));
          if (page) {
            page.groups = clusterParentUpdate.page.groups.filter((id: string) => page.groups.includes(id));
            arr.set(clusterParentUpdate.page.id, page);
          } else {
            arr.set(clusterParentUpdate.page.id, clusterParentUpdate.page);
          }
          return arr;
        }, new Map<string, FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training>());
        const pages = Array.from(pageMap.values());
        if (pages.length > 0) {
          return forkJoin(pages.map((page) =>
            store.dispatch(new FlatActions.AnnotationGroupEntityActions.RearrangeGroups(page.groups)).pipe(
              mergeMap(() =>
                store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
              )
            )
          ));
        }
        return of(data);
      }),
    );
  }
  
  static removeAnnotationFromGroup(store: Store,
                                   id: string,
                                   groupId: string,
                                   clusterParentUpdate = false) {
    const typedGroups = store.selectSnapshot(FlatStates.AnnotationGroupEntityState.typedEntities$);
    const group = structuredClone(typedGroups[groupId]);
    
    // Check Index
    const index = group.annotations.findIndex((a: string) => a === id);
    if (index < 0) {
      return of({});
    }
    
    // Update Group
    group.annotations.splice(index, 1);
    
    // Remove Annotation From Group
    return of({group}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.AnnotationGroupEntityActions.UpdateEntity(group))
      }),
    );
  }
  
  static removeAnnotationsFromGroup(store: Store,
                                    ids: string[],
                                    groupId: string,
                                    clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of({groupId});
    }
    
    const typedGroups = store.selectSnapshot(FlatStates.AnnotationGroupEntityState.typedEntities$);
    const group = structuredClone(typedGroups[groupId]);
    
    for (const id of ids) {
      // Check Index
      const index = group.annotations.findIndex((a: string) => a === id);
      if (index > -1) {
        // Update Group
        group.annotations.splice(index, 1);
      }
    }
    
    // Remove Annotation From Group
    return of({group}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.AnnotationGroupEntityActions.UpdateEntity(group))
      }),
    );
  }

  //</editor-fold>
}
